import { useEffect, useState } from "react";
import { Loading } from "../components/Loading";
import { useClickCreateOauthConnectionUx } from "../logic/hooks/ux/useClickCreateOauthConnectionUx";
import { IntegrationCreatedSection } from "../sections/IntegrationCreatedSection";
import { ConnectionFailedSection } from "../sections/ConnectionFailedSection";
import { useGetConnectionDefinition } from "../logic/hooks/useGetConnectionDefinition";
import useGetConnectionOauthDefinition from "../logic/hooks/useGetConnectionOauthDefinition";
import { ConnectionFormSection } from "../sections/ConnectionFormSection";
import { useForm } from "react-hook-form";
import axios, { AxiosResponse } from "axios";
import { apiKeys } from "../logic/apis";

export const ControlledOauthConnection = ({
  integrationId,
  setIntegrationId,
  secretId,
  setClientId,
  showNameInput,
  selectedConnection,
  connectionGuide,
  requestId,
  linkHeaders,
  imageUrl,
  companyName,
  isWhiteLabel,
}: {
  integrationId: number;
  setIntegrationId: (id: number | undefined) => void;
  secretId: string;
  setClientId: (id: string | undefined) => void;
  showNameInput?: boolean;
  selectedConnection?: string | number;
  connectionGuide?: string;
  requestId: number;
  linkHeaders?: Record<string, unknown>;
  imageUrl?: string;
  companyName?: string;
  isWhiteLabel?: boolean | null;
}) => {
  const { definition, isLoading: isLoadingDefinition } =
    useGetConnectionDefinition({
      definitionId: integrationId,
      linkHeaders,
    });

  const { connectionOauthDefinition, isLoading: isLoadingOauthDefinition } = useGetConnectionOauthDefinition(
    definition?.platform || '',
    linkHeaders
  );

  const handleClickBack = async () => {
    setIntegrationId(undefined);
    setClientId(undefined);
  };

  const handleClose = async () => {
    window?.parent?.postMessage(
      {
        messageType: "EXIT_EVENT_LINK",
      },
      "*"
    );
  };
  const [errorMessage, setErrorMessage] = useState<string>();
  const [success, setSuccess] = useState<boolean>();

  const { trigger, state } = useClickCreateOauthConnectionUx(setErrorMessage, setSuccess, linkHeaders);

  const [isLoading, setLoading] = useState<boolean>();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const onSubmit = (formData: { [K: string]: unknown }) => {

    if (connectionOauthDefinition?.id) {
      trigger({
        secretId,
        requestId,
        formData,
        setLoading,
        connectionOauthDefinitionId: connectionOauthDefinition?.id
      })
    }

  };

  useEffect(() => {
    let timeoutId: any;
    const checkResponse = async () => {
      try {
        const result: AxiosResponse = await axios.get(
          `${apiKeys["check.connection.state"]}?state=${state}`,
          {
            withCredentials: true,
          }
        );

        const status = result?.data?.status;

        if (status === "success") {
          setSuccess(true);
          setLoading(false);
          window.parent.postMessage(
            {
              messageType: "LINK_SUCCESS",
              message: result?.data?.connection
            },
            "*"
          );
        } else if (status === "failure") {
          setErrorMessage(result?.data?.error);
          setLoading(false);
          window.parent.postMessage(
            {
              messageType: "LINK_ERROR",
              message: result?.data?.error,
            },
            "*"
          );
        } else {
          timeoutId = setTimeout(checkResponse, 5000);
        }
      } catch (error) {
        console.error("Error checking response", error);
        setErrorMessage("Something went wrong");
        setLoading(false);
      }
    };

    if (isLoading && state) {
      checkResponse();
    }

    return () => {
      clearTimeout(timeoutId);
    };
  }, [isLoading, state]);

  const handleClick = () => {
    setErrorMessage(undefined);
  };

  return (
    <>
      {(isLoadingDefinition || isLoadingOauthDefinition) && <Loading onClose={handleClose} />}
      {success && <IntegrationCreatedSection onClose={handleClose} />}
      {errorMessage && (
        <ConnectionFailedSection
          errorMessage={errorMessage}
          onClose={handleClose}
          onClick={handleClick}
          onBack={handleClickBack}
          isConnectionSelected={!!selectedConnection}
        />
      )}
      {definition && connectionOauthDefinition && !success && !errorMessage && (
        <ConnectionFormSection
          onSubmit={onSubmit}
          definition={definition}
          onBack={handleClickBack}
          onClose={handleClose}
          register={register}
          handleSubmit={handleSubmit}
          errors={errors}
          error={errorMessage}
          showNameInput={showNameInput}
          isLoading={isLoading}
          isConnectionSelected={!!selectedConnection}
          helperLink={connectionGuide === "" ? undefined : (connectionGuide || definition?.frontend?.spec?.helperLink)}
          platformImageUrl={imageUrl}
          showAvatars={true}
          companyName={companyName}
          isWhiteLabel={isWhiteLabel}
        />
      )}
    </>
  );
};
